import React, { useEffect, useState } from 'react';
import { Avatar, Button, Dropdown, Form, Image, Input, Modal, Space, Upload, notification } from 'antd';
import { CameraOutlined, PictureOutlined } from '@ant-design/icons';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Compressor from 'compressorjs';
import MobileDetect from 'mobile-detect';
import EntranceMotion from '../../../helpers/components/entrance_motion';
import globalHelper from '../../../helpers/functions/global.helper';
import passService from '../../../services/pass/pass.service';

const acceptable_file_format = ['image/jpeg', 'image/png'];
const isAndroid = (new MobileDetect(window.navigator.userAgent)?.os() === 'AndroidOS');
const isWebView = (/(android)/i.test(navigator.userAgent) && /(wv)|(webview)/i.test(navigator.userAgent));

const PassDetail = ({ pass, is_expired, is_checked_in, onRefresh }) => {
    const [form] = Form.useForm();
    const [state, setState] = useState({
        file: null,
        save_loading: false,
    });

    const beforeUpload = async (file) => {
        const is_image = acceptable_file_format?.find(x => x === file?.type);
        if (is_image) {
            const uploaded_file = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = () => {
                    file.base64 = reader.result.split(',')[1];
                    resolve(file);
                };
                reader.onerror = (error) => reject(error);
            });
            setState(state => ({ ...state, file: `data:image/jpeg;base64,${uploaded_file?.base64}` }));
        } else {
            errorModal("Please upload in image format");
        }
        return false;
    }

    const savePass = async () => {
        const { name, avatar } = form.getFieldsValue();
        setState(state => ({ ...state, save_loading: true }));

        const file = avatar?.file ?? null;
        let compressed_file = null;
        if (file) {
            compressed_file = await new Promise((resolve, reject) =>
                new Compressor(file, {
                    quality: 0.8,
                    height: 800,
                    width: 600,
                    mimeType: 'image/jpeg',
                    success: resolve,
                    error: reject,
                })
            );
        }

        const form_data = new FormData();
        compressed_file && form_data.append('avatar', compressed_file);
        name && form_data.append('name', name);

        const updateResponse = await passService.updatePass(pass?.pass_code, form_data).catch(err => {
            const message = err?.response?.data?.message;
            errorModal(message);
            setState(state => ({ ...state, save_loading: false, file: null }));
        });

        if (updateResponse) {
            setTimeout(() => {
                setState(state => ({ ...state, save_loading: false }));
                notification.success({
                    message: 'Success',
                    description: 'Your pass has been updated',
                });
                onRefresh();
            }, 500);
        }
    }

    const errorModal = (message) => {
        Modal.error({
            icon: null,
            title: message,
            content: (
                <>
                    <div style={{ padding: 24, textAlign: 'center' }}>
                        <Image preview={false} src='/cibanners/selfie-person.png' style={{ maxWidth: 180 }} />
                    </div>
                    <div>
                        <span className='header-font' style={{ fontWeight: 500, fontSize: 16 }}>{message}</span>
                    </div>
                    <div>
                        <span>Please upload another photo or make a selfie with your device's camera.</span>
                    </div>
                </>
            ),
            okButtonProps: { style: { background: 'var(--main-color)', color: '#fff' } },
        });
        form.setFieldsValue({ avatar: null });
    }

    useEffect(() => {
        form.setFieldsValue({ name: pass?.name });
        setState(state => ({ ...state, file: pass?.url_image }));
    }, [pass]);

    return (
        <>
            <div className='save-ticket-user-content'>
                <div>
                    <span className='ticket-label ticket-title h1' style={{ fontWeight: 'bold', fontSize: globalHelper?.isMobileScreen() ? 20 : 24 }}>
                        Upload Selfie
                    </span>
                </div>

                {
                    is_expired &&
                    (
                        <div style={{ marginTop: 16 }}>
                            <span style={{ color: 'var(--secondary-text-color)' }}>This pass has expired and can no longer be updated.</span>
                        </div>
                    )
                }

                <div style={{ marginTop: 40 }}>
                    <Form id="pass-detail-form" layout='vertical' form={form} onFinish={savePass}>
                        <div>
                            <EntranceMotion initialY={-100} duration={1}>
                                {
                                    isAndroid ?
                                    (
                                        <div style={{ width: 'fit-content', margin: 'auto' }}>
                                            <Dropdown
                                                disabled={is_expired || is_checked_in}
                                                trigger={['click']}
                                                menu={{
                                                    items: [
                                                        {
                                                            key: '1',
                                                            style: { padding: 0 },
                                                            label: (
                                                                <Form.Item className='dropdown-image' name={'avatar'} style={{ marginBottom: 0, minHeight: 0 }}>
                                                                    <Upload
                                                                        disabled={is_expired || is_checked_in}
                                                                        beforeUpload={beforeUpload}
                                                                        name="image"
                                                                        showUploadList={false}
                                                                        accept='image/jpeg, image/png'
                                                                        style={{ width: '100%' }}
                                                                    >
                                                                        <Space style={{ width: '100%', padding: '5px 12px' }}>
                                                                            <PictureOutlined style={{ color: 'var(--secondary-text-color)' }} />
                                                                            <span style={{ color: 'var(--secondary-text-color)' }}>Photo Library</span>
                                                                        </Space>
                                                                    </Upload>
                                                                </Form.Item>
                                                            ),
                                                        },
                                                        { type: 'divider' },
                                                        {
                                                            key: '2',
                                                            style: { padding: 0 },
                                                            label: (
                                                                <div style={{ paddingBlock: 5 }}>
                                                                    {
                                                                        isWebView ?
                                                                        (
                                                                            <div onClick={() => window.location = `intent:${window.location.href}#Intent;end`} style={{ paddingInline: 12 }}>
                                                                                <Space style={{ width: '100%', opacity: 0.5 }}>
                                                                                    <CameraOutlined style={{ color: 'var(--secondary-text-color)' }} />
                                                                                    <span style={{ color: 'var(--secondary-text-color)' }}>Take Photo</span>
                                                                                </Space>
                                                                            </div>
                                                                        )
                                                                        :
                                                                        (
                                                                            <Form.Item className='dropdown-image' name={'avatar'} style={{ marginBottom: 0, minHeight: 0 }}>
                                                                                <Upload
                                                                                    disabled={is_expired || is_checked_in}
                                                                                    beforeUpload={beforeUpload}
                                                                                    name="image"
                                                                                    showUploadList={false}
                                                                                    accept='image/*'
                                                                                    capture
                                                                                    style={{ width: '100%' }}
                                                                                >
                                                                                    <Space style={{ width: '100%', paddingInline: 12 }}>
                                                                                        <CameraOutlined style={{ color: 'var(--secondary-text-color)' }} />
                                                                                        <span style={{ color: 'var(--secondary-text-color)' }}>Take Photo</span>
                                                                                    </Space>
                                                                                </Upload>
                                                                            </Form.Item>
                                                                        )
                                                                    }
                                                                </div>
                                                            ),
                                                        },
                                                    ],
                                                }}
                                            >
                                                <Form.Item>
                                                    <div className="border-circle" style={{ border: '2px solid var(--main-color)', borderRadius: '50%', padding: 12, cursor: 'pointer' }}>
                                                        <Avatar size={140} src={<LazyLoadImage src={state?.file ?? "/cibanners/empty-user.png"} />} />
                                                    </div>
                                                </Form.Item>
                                            </Dropdown>
                                        </div>
                                    )
                                    :
                                    (
                                        <Form.Item name={'avatar'} style={{ marginBottom: 12 }}>
                                            <Upload
                                                disabled={is_expired || is_checked_in}
                                                beforeUpload={beforeUpload}
                                                name="image"
                                                showUploadList={false}
                                                className="upload-circle"
                                                accept='image/jpeg, image/png'
                                            >
                                                <div className="border-circle" style={{ border: '2px solid var(--main-color)', borderRadius: '50%', padding: 12 }}>
                                                    <Avatar size={140} src={<LazyLoadImage src={state?.file ?? "/cibanners/empty-user.png"} />} />
                                                </div>
                                            </Upload>
                                        </Form.Item>
                                    )
                                }
                            </EntranceMotion>

                            {
                                is_checked_in &&
                                (
                                    <div style={{ marginTop: 12, textAlign: 'center' }}>
                                        <span style={{ color: 'var(--secondary-text-color)', fontWeight: 500 }}>This pass has already been checked in.</span>
                                    </div>
                                )
                            }

                            <Form.Item name={'name'} label={'Name'} className='ticket_user-form-item' style={{ marginTop: 12 }}>
                                <Input disabled placeholder='John Doe' style={{ border: '2px solid #cbd5e0' }} />
                            </Form.Item>

                            {
                                !is_expired && !is_checked_in &&
                                (
                                    <Form.Item style={{ marginTop: 40 }}>
                                        {
                                            globalHelper?.isMobileScreen() ?
                                            (
                                                <>
                                                    <div style={{ marginTop: 80 }} />
                                                    <div className='footer-save-ticket-user'>
                                                        <Button loading={state?.save_loading} onClick={() => form.submit()} type='text' size='large' style={{ width: '100%' }}>
                                                            <span style={{ fontWeight: 500, color: 'var(--main-text-color)' }}>Save</span>
                                                        </Button>
                                                    </div>
                                                </>
                                            )
                                            :
                                            (
                                                <div style={{ display: 'flex', justifyContent: 'center' }}>
                                                    <Button loading={state?.save_loading} onClick={() => form.submit()} className='footer-save-ticket-user' type='primary' size='large'>Save</Button>
                                                </div>
                                            )
                                        }
                                    </Form.Item>
                                )
                            }
                        </div>
                    </Form>
                </div>
            </div>
        </>
    );
}

export default PassDetail;
